
import { Ball, SimulationConfig } from './types';
import { ShapeUtils } from './ShapeUtils';

export const drawAdvancedBall = (
  ctx: CanvasRenderingContext2D,
  b: Ball,
  config: SimulationConfig,
  rgb: string
) => {
  ctx.save();

  // 1. Resolve Colors
  const useRGB = config.rgbLighting && config.useBallRGB;
  const mainColor = useRGB ? rgb : b.color;
  const shape = config.ballShape || 'CIRCLE';
  
  // 2. Physics-based Deformation (Squash & Stretch) - Only apply if Circle for now as it complicates shapes
  if (shape === 'CIRCLE' && config.ballSquashStretch) {
      const velMag = Math.sqrt(b.vel.x * b.vel.x + b.vel.y * b.vel.y);
      if (velMag > 0.5) {
          const angle = Math.atan2(b.vel.y, b.vel.x);
          const maxStretch = 1.0 + (config.ballSquashFactor * 0.5); // Max 1.5x stretch
          const stretch = Math.min(maxStretch, 1.0 + velMag * 0.02 * config.ballSquashFactor);
          const squash = 1.0 / Math.sqrt(stretch); // Maintain area approximation
          
          ctx.translate(b.pos.x, b.pos.y);
          ctx.rotate(angle);
          ctx.scale(stretch, squash);
          ctx.translate(-b.pos.x, -b.pos.y);
      }
  }

  // 3. Pulse Effect (Visual Size oscillation)
  let pulseScale = 1.0;
  if (config.ballPulseSpeed > 0) {
      pulseScale = 1.0 + Math.sin(Date.now() * 0.005 * config.ballPulseSpeed) * 0.1;
  }
  const r = b.radius * pulseScale;

  // 4. Rotation Handling
  // We translate to center, rotate, then draw at (0,0)
  ctx.translate(b.pos.x, b.pos.y);
  if (shape !== 'CIRCLE') {
      ctx.rotate(b.angle || 0);
  }

  // 5. Glow Engine
  if (config.ballGlowRadius > 0) {
      ctx.shadowBlur = config.ballGlowRadius;
      ctx.shadowColor = config.ballGlowColor === '#ffffff' ? mainColor : config.ballGlowColor; 
      ctx.globalAlpha = config.ballGlowOpacity;
  }

  // 6. Main Shape Path
  if (shape === 'CIRCLE') {
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
  } else {
      // ShapeUtils trace expects absolute, but we are translated.
      // Pass (0,0) as center
      ShapeUtils.traceShape(ctx, {x:0, y:0}, shape, r);
  }

  // 7. Fill Logic
  ctx.globalAlpha = config.ballFillOpacity;
  
  if (config.ballFillType === 'HOLLOW') {
      // Do nothing for fill
  } else if (config.ballFillType === 'GRADIENT_RADIAL') {
      const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, r * 0.1, 0, 0, r);
      grad.addColorStop(0, config.ballGradientInner === '#ffffff' ? '#ffffff' : config.ballGradientInner);
      grad.addColorStop(1, mainColor);
      ctx.fillStyle = grad;
      ctx.fill();
  } else if (config.ballFillType === 'GRADIENT_LINEAR') {
      const grad = ctx.createLinearGradient(-r, -r, r, r);
      grad.addColorStop(0, config.ballGradientInner === '#ffffff' ? mainColor : config.ballGradientInner);
      grad.addColorStop(1, config.ballGradientOuter === '#000000' ? '#000000' : config.ballGradientOuter);
      ctx.fillStyle = grad;
      ctx.fill();
  } else {
      if (config.ballStyle === 'NEON') {
           ctx.fillStyle = mainColor;
           ctx.shadowBlur = r * 1.5;
           ctx.shadowColor = mainColor;
      } else if (config.ballStyle === 'CHROME') {
           const g = ctx.createLinearGradient(-r, -r, r, r);
           g.addColorStop(0, '#fff'); g.addColorStop(0.5, mainColor); g.addColorStop(1, '#111');
           ctx.fillStyle = g;
      } else {
           ctx.fillStyle = mainColor;
      }
      ctx.fill();
  }

  // 8. Texture Overlay (Masked to shape)
  if (config.ballTexture !== 'NONE') {
      ctx.save();
      ctx.clip(); // Clip to the current path (the shape)
      ctx.globalAlpha = config.ballTextureOpacity;
      ctx.fillStyle = '#000000'; 
      
      if (config.ballTexture === 'STRIPES') {
          const step = 4;
          ctx.beginPath();
          for(let i = -r; i < r; i+=step*2) ctx.rect(i, -r, step, r*2);
          ctx.fill();
      } else if (config.ballTexture === 'DOTS') {
          const step = 6;
          ctx.beginPath();
          for(let x = -r; x < r; x+=step) {
              for(let y = -r; y < r; y+=step) {
                 if ((Math.floor(x/step) + Math.floor(y/step)) % 2 === 0) ctx.rect(x, y, 2, 2);
              }
          }
          ctx.fill();
      } else if (config.ballTexture === 'CROSSHATCH') {
           ctx.strokeStyle = 'rgba(0,0,0,0.5)';
           ctx.lineWidth = 1;
           ctx.beginPath();
           for(let i = -r; i < r; i+=4) {
               ctx.moveTo(i, -Math.sqrt(r*r - i*i)); // Only approx for circle, works visually for others mostly or gets clipped
               ctx.lineTo(i, Math.sqrt(r*r - i*i));
           }
           ctx.stroke();
      }
      ctx.restore();
  }

  // 9. Stroke / Border
  if (config.ballStyle !== 'NEON' && config.ballGlowRadius === 0) {
      ctx.shadowBlur = 0;
  }

  if (config.ballStrokeWidth > 0 || config.ballFillType === 'HOLLOW') {
      const strokeW = Math.max(config.ballStrokeWidth, config.ballFillType === 'HOLLOW' ? 2 : 0);
      if (strokeW > 0) {
        ctx.globalAlpha = config.ballStrokeOpacity;
        ctx.lineWidth = strokeW;
        ctx.strokeStyle = config.ballStrokeColor === '#ffffff' ? (useRGB ? rgb : '#ffffff') : config.ballStrokeColor;
        
        // Re-trace path for stroking (since texture clip might have messed with context or path)
        if (shape === 'CIRCLE') {
            ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2);
        } else {
            ShapeUtils.traceShape(ctx, {x:0, y:0}, shape, r);
        }
        ctx.stroke();
      }
  }

  ctx.restore();
};
